import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { fetchCandidate } from "services/watoms/professionalExamination";
import { updateCandidateUser } from "services/watoms/watomsData";
import person from '../../../assets/person.jpg';
import { fetchWatomsRelatedVtcs } from "services/watoms/watomsData";
import { cairoDate } from "utils/cairoDate";
import { FaCamera, FaTimes, FaCheck, FaSpinner } from "react-icons/fa";
import toast from "react-hot-toast";

const DOC_TYPES = [
    { key: "candidate_picture", label: "Candidate Picture" },
    { key: "candidate_passport", label: "Passport Image" },
    { key: "candidate_ticket", label: "Ticket Image" },
    { key: "candidate_picture_passport", label: "Picture with Passport" },
    { key: "candidate_picture_ticket", label: "Picture with Ticket" },
    { key: "fc_back_img", label: "FC Back Image" },
    { key: "fc_side_img", label: "FC Side Image" },
    { key: "practical_back_img", label: "Practical Back Image" },
    { key: "practical_side_img", label: "Practical Side Image" },
    { key: "theory_back_img", label: "Theory Back Image" },
    { key: "theory_side_img", label: "Theory Side Image" },
];

const WatomsPECandidateProfile = () => {
    const { id } = useParams();
    const [vtcs, setVtcs] = useState([]);
    const [candidate, setCandidate] = useState({});
    const [activeCameraField, setActiveCameraField] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const [stream, setStream] = useState(null);

    // change this to localhost when work in your machine
    const API_BASE = process.env.REACT_APP_BACKEND_BASE_URL || "http://localhost:4000";

    const fileUrl = (p) => {
        if (!p) return null;
        if (p.startsWith("http")) return p;
        // if your DB stores "uploads/pe/xx.jpg" or "/uploads/pe/xx.jpg"
        return `${API_BASE}/${p.replace(/^\/+/, "")}`;
    };

    useEffect(() => {
        const loadVtcs = async () => {
            const response = await fetchWatomsRelatedVtcs();
            setVtcs(response);
        };

        loadCandidateData();
        loadVtcs();
    }, [id]);

    const loadCandidateData = async () => {
        try {
            const response = await fetchCandidate(id);
            setCandidate(response);
        } catch (error) {
            console.error("Failed to load candidate", error);
        }
    };

    const startCamera = async (field) => {
        setActiveCameraField(field);
        try {
            const mediaStream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: "environment" }
            });
            setStream(mediaStream);
            if (videoRef.current) {
                videoRef.current.srcObject = mediaStream;
            }
        } catch (err) {
            console.error("Error accessing camera:", err);
            toast.error("Could not access camera");
            setActiveCameraField(null);
        }
    };

    const stopCamera = () => {
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            setStream(null);
        }
        setActiveCameraField(null);
    };

    const captureAndSubmit = async () => {
        if (!videoRef.current || !canvasRef.current || !activeCameraField) return;

        const video = videoRef.current;
        const canvas = canvasRef.current;
        const context = canvas.getContext("2d");

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        canvas.toBlob(async (blob) => {
            if (!blob) return;

            const formData = new FormData();
            formData.append(activeCameraField, blob, `${activeCameraField}.jpg`);

            setIsSubmitting(true);
            try {
                await updateCandidateUser(formData, id);
                toast.success("Image uploaded successfully!");
                loadCandidateData(); // Refresh data
                stopCamera();
            } catch (error) {
                console.error("Upload failed", error);
                toast.error("Failed to upload image");
            } finally {
                setIsSubmitting(false);
            }
        }, "image/jpeg", 0.8);
    };

    return (
        <div className="min-h-screen bg-slate-50" dir="rtl">
            {/* Navbar */}
            <div className="border-b bg-white">
                <div className="mx-auto w-full max-w-6xl px-4 py-5 sm:px-6">
                    <h1 className="text-xl font-bold text-slate-900">Professional Exam Candidate Profile</h1>
                </div>
            </div>

            {/* Camera Modal */}
            {activeCameraField && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90">
                    <div className="relative w-full max-w-md h-full max-h-screen flex flex-col">
                        <div className="flex justify-between items-center p-4 bg-black text-white">
                            <h3 className="text-lg font-bold">Capture {DOC_TYPES.find(d => d.key === activeCameraField)?.label}</h3>
                            <button onClick={stopCamera} className="p-2 bg-gray-800 rounded-full">
                                <FaTimes />
                            </button>
                        </div>
                        
                        <div className="relative flex-1 bg-black overflow-hidden flex items-center justify-center">
                            <video
                                ref={videoRef}
                                autoPlay
                                playsInline
                                className="w-full h-full object-cover"
                            />
                            {/* Green Box Overlay */}
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <div className="w-3/4 h-1/2 border-4 border-green-500 rounded-lg shadow-[0_0_0_9999px_rgba(0,0,0,0.5)] relative">
                                    <div className="absolute top-0 left-0 bg-green-500 text-white text-xs px-2 py-1">
                                        Ensure text is clear
                                    </div>
                                    <div className="absolute bottom-0 right-0 bg-green-500 text-white text-xs px-2 py-1">
                                        Good lighting
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="p-6 bg-black flex justify-center">
                            <button
                                onClick={captureAndSubmit}
                                disabled={isSubmitting}
                                className="w-20 h-20 bg-white rounded-full border-4 border-gray-300 flex items-center justify-center hover:bg-gray-200 focus:outline-none"
                            >
                                {isSubmitting ? <FaSpinner className="animate-spin text-2xl text-black" /> : <div className="w-16 h-16 bg-red-600 rounded-full" />}
                            </button>
                        </div>
                    </div>
                    {/* Hidden canvas for capturing */}
                    <canvas ref={canvasRef} className="hidden" />
                </div>
            )}

            {/* Candidate detailed profile */}
            <div className="mx-auto w-full max-w-6xl px-4 py-6 sm:px-6">
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
                    {/* right column */}
                    <aside className="lg:col-span-4">
                        <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
                            <div className="flex items-center gap-4">
                                <div className="relative h-16 w-16 overflow-hidden rounded-2xl ring-1 ring-slate-200">
                                    <img
                                        src={candidate?.profilePic || person}
                                        alt={`${candidate?.name} Picture`}
                                        className="h-full w-full object-cover"
                                        loading="lazy"
                                    />
                                </div>
                                <div className="min-w-0">
                                    <p className="truncate text-lg font-semibold text-slate-900">{candidate?.name}</p>
                                </div>
                            </div>

                            <div className="mt-5 space-y-3">
                                <InfoRow label="الجنسية" value={candidate?.nationality} />
                                <InfoRow label="المركز" value={vtcs.find(vtc => vtc.id === candidate?.organization_id)?.name || "-"} />
                                <InfoRow label="الدولة المقدم لها" value={candidate?.recommended_country} />
                                <InfoRow label="اختبار السمات و الكقاءة" value={cairoDate(candidate.fc_start_date)} />
                                <InfoRow label="اختبار العملي" value={cairoDate(candidate.practical_start_date)} />
                                <InfoRow label="اختبار النظري" value={cairoDate(candidate.theory_start_date)} />
                            </div>

                            <div className="mt-5 border-t pt-4">
                                <p className="text-sm font-semibold text-slate-900">Contact</p>
                                <div className="mt-3 space-y-2">
                                    <InfoRow label="البريد الالكتروني" value={candidate.email} />
                                    <InfoRow label="رقم الموبيل" value={candidate.phone_number} />
                                </div>
                            </div>
                        </div>
                    </aside>

                    {/* left column */}
                    <section className="lg:col-span-8 space-y-6">
                        {/* more details */}
                        <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <h2 className="text-base font-semibold text-slate-900">بيانات المتقدم</h2>
                                </div>
                                <div className="rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-700 ring-1 ring-slate-200">
                                    اخر تحديث: <span className="font-medium">2026-02-01</span>
                                </div>
                            </div>

                            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <MiniStat title="رقم الممتحن" value={candidate.id} />
                                <MiniStat title="رمز المهنة" value={candidate.profession_code} />
                                <MiniStat title="المهنة" value={candidate.profession} />
                                <MiniStat title="جواز السفر" value={candidate.passport_number} />
                                <MiniStat title="الرقم القومي" value={candidate.id_number} />
                                <MiniStat title="التخضض" value={candidate.category} />
                            </div>
                        </div>

                        {/* Document Upload Section */}
                        <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
                            <h2 className="text-base font-semibold text-slate-900 mb-4">Document Upload</h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                                {DOC_TYPES.map((doc) => {
                                    const hasFile = !!candidate[doc.key];
                                    return (
                                        <div key={doc.key} className="relative rounded-xl border border-slate-200 p-3 bg-slate-50 hover:bg-slate-100 transition-colors">
                                            <div className="flex justify-between items-start mb-2">
                                                <span className="text-sm font-medium text-slate-700">{doc.label}</span>
                                                {hasFile && <FaCheck className="text-green-500" />}
                                            </div>
                                            
                                            <div className="aspect-video bg-gray-200 rounded-lg mb-3 overflow-hidden">
                                                {hasFile ? (
                                                    <img src={fileUrl(candidate[doc.key])} alt={doc.label} className="w-full h-full object-cover" />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                                                        <span className="text-xs">No Image</span>
                                                    </div>
                                                )}
                                            </div>

                                            <button
                                                onClick={() => startCamera(doc.key)}
                                                className="w-full py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center justify-center gap-2 text-sm transition-colors"
                                            >
                                                <FaCamera />
                                                {hasFile ? "Retake" : "Capture"}
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Timeline */}
                        <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
                            <h2 className="text-base font-semibold text-slate-900">Exam Timeline</h2>
                            <p className="mt-1 text-sm text-slate-500">Key events during the exam day.</p>

                            <div className="mt-4 space-y-3">
                                {candidate?.timeline?.map((item) => (
                                    <div
                                        key={item.time + item.title}
                                        className="flex gap-3 rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200"
                                    >
                                        <div className="w-16 shrink-0 text-sm font-semibold text-slate-900">
                                            {item.time}
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-sm font-semibold text-slate-900">{item.title}</p>
                                            <p className="mt-1 text-sm text-slate-600">{item.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

function InfoRow({ label, value }) {
    return (
        <div className="flex items-center justify-between gap-3">
            <span className="text-sm text-slate-500">{label}</span>
            <span className="text-sm font-medium text-slate-900">{value}</span>
        </div>
    );
}

function MiniStat({ title, value }) {
    return (
        <div className="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200">
            <p className="text-xs font-medium text-slate-500">{title}</p>
            <p className="mt-1 text-sm font-semibold text-slate-900">{value}</p>
        </div>
    );
}

export default WatomsPECandidateProfile;